// The pieces every list page (Shipments, Quotations) is made of: header, search + filters, footer.
import type { ReactNode } from 'react';
import { Box, Button, InputAdornment, MenuItem, Pagination, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useNotice } from './NoticeProvider';

/** Title, subtitle and the buttons on the right: "New …", Export, and anything extra */
export function PageHeader({
  title,
  subtitle,
  newLabel,
  onNew,
  noExport,
  children,
}: {
  title: string;
  subtitle: string;
  /** the label of the "New …" button; leave out for a page whose records are written elsewhere */
  newLabel?: string;
  /** what "New …" does; without it a "not available yet" message shows */
  onNew?: () => void;
  /** leave out the Export button */
  noExport?: boolean;
  children?: ReactNode;
}) {
  const notice = useNotice();
  const soon = () => notice('This is not available yet. It arrives when the server is connected.', 'info');
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        <Typography color="text.secondary">{subtitle}</Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
        {newLabel && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNew ?? soon}>
            {newLabel}
          </Button>
        )}
        {!noExport && (
          <Button variant="outlined" color="inherit" startIcon={<FileDownloadOutlined />} onClick={soon}>
            Export
          </Button>
        )}
        {children}
      </Stack>
    </Stack>
  );
}

/** A text box with a magnifying glass */
export function SearchField({
  placeholder,
  value,
  onChange,
  sx,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  sx?: object;
}) {
  return (
    <TextField
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
      sx={sx}
    />
  );
}

/** The search box with the filter dropdowns beside it (children) */
export function FilterBar({
  placeholder,
  search,
  onSearch,
  children,
}: {
  placeholder: string;
  search: string;
  onSearch: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1}>
      <SearchField
        placeholder={placeholder}
        value={search}
        onChange={onSearch}
        sx={{ flex: { lg: '1 1 160px' }, minWidth: { lg: 160 }, maxWidth: { lg: 300 } }}
      />
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: 'wrap', rowGap: 1, alignItems: 'center', flexShrink: { lg: 0 } }}
      >
        {children}
      </Stack>
    </Stack>
  );
}

/** A dropdown with "All" first */
export function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  return (
    <TextField select label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ width: 128 }}>
      <MenuItem value="all">All</MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  );
}

type FooterProps = {
  noun: string;
  from: number;
  count: number;
  total: number;
  page: number;
  pages: number;
  onPage: (p: number) => void;
  /** optional "Show 15" box */
  size?: number;
  sizes?: number[];
  onSize?: (n: number) => void;
};

/** "Showing 1 to 8 of 10 shipments" with the page buttons */
export function ListFooter({ noun, from, count, total, page, pages, onPage, size, sizes, onSize }: FooterProps) {
  if (!total) return null;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography variant="body2" color="text.secondary">
        Showing {from + 1} to {from + count} of {total} {noun}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {sizes && onSize && (
          <TextField
            select
            label="Show"
            value={size}
            onChange={(e) => onSize(Number(e.target.value))}
            sx={{ width: 90 }}
          >
            {sizes.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Pagination count={pages} page={page} onChange={(_e, p) => onPage(p)} color="primary" />
      </Stack>
    </Stack>
  );
}

/** The footer for a list shown in fixed-size pages: it works out "Showing 1 to 8" and the page count itself */
export function PagedFooter({
  noun,
  total,
  page,
  size,
  onPage,
}: {
  noun: string;
  total: number;
  page: number;
  size: number;
  onPage: (p: number) => void;
}) {
  const from = (page - 1) * size;
  return (
    <ListFooter
      noun={noun}
      from={from}
      count={Math.min(size, total - from)}
      total={total}
      page={page}
      pages={Math.max(1, Math.ceil(total / size))}
      onPage={onPage}
    />
  );
}
